import { supabase } from '@/integrations/supabase/client';
import { setConnectionStatus, removeConnectionSource } from './connectionStatus';
import { onAppResume } from './appLifecycle';
import { isOfflineMode, queueOfflineAction } from './offlineSync';
import { safeSetItem } from '@/lib/safeStorage';

// UID format validation: XXXX-XXXX-XXXX-XXXX with alphanumeric chars only
const UID_REGEX = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

function validateUidFormat(uid: string): boolean {
  return UID_REGEX.test(uid);
}

function assertValidUid(uid: string, name: string): void {
  if (!validateUidFormat(uid)) {
    throw new Error(`Invalid ${name} format`);
  }
}

// Defense-in-depth: Explicitly escape UID values before use in filter strings
function escapePostgrestValue(value: string): string {
  return value.replace(/[^A-Z0-9-]/gi, '').toUpperCase();
}

function sanitizeUid(uid: string): string | null {
  if (!validateUidFormat(uid)) return null;
  return escapePostgrestValue(uid);
}

export interface DbUser {
  uid: string;
  signing_public_key: string;
  encryption_public_key: string;
}

export interface DbMessage {
  id: string;
  sender_uid: string;
  recipient_uid: string | null;
  group_id: string | null;
  encrypted_content: string;
  nonce: string;
  burn_after_view: boolean;
  delivered: boolean;
  created_at: string;
}

export interface DbGroup {
  id: string;
  name: string;
  topic: string | null;
  created_by: string;
}

export async function registerUser(
  uid: string,
  signingPublicKey: string,
  encryptionPublicKey: string
): Promise<{ success: boolean; error?: string }> {
  assertValidUid(uid, 'UID');
  
  const { error } = await supabase
    .from('users')
    .insert({
      uid,
      signing_public_key: signingPublicKey,
      encryption_public_key: encryptionPublicKey,
    });

  if (error) {
    if (error.code === '23505') {
      return { success: true };
    }
    return { success: false, error: error.message };
  }
  return { success: true };
}

export async function lookupUser(uid: string): Promise<DbUser | null> {
  if (!validateUidFormat(uid)) return null;

  try {
    const { data, error } = await supabase
      .from('users')
      .select('uid, signing_public_key, encryption_public_key')
      .eq('uid', uid)
      .maybeSingle();

    if (error || !data) return null;
    return data as DbUser;
  } catch {
    // Defensive — never throw from lookupUser; legacy callers expect null on failure.
    return null;
  }
}

/**
 * Strict lookup: throws on transient errors (network, 5xx) and returns null
 * ONLY when the server confirms the user does not exist (0 rows).
 * Use this when you need to distinguish "definitely missing" from "could not check".
 */
async function lookupUserStrict(uid: string): Promise<DbUser | null> {
  if (!validateUidFormat(uid)) return null;
  const { data, error } = await supabase
    .from('users')
    .select('uid, signing_public_key, encryption_public_key')
    .eq('uid', uid)
    .maybeSingle();

  if (error) {
    throw new Error(`lookupUser failed: ${error.message || 'unknown error'}`);
  }
  return (data as DbUser) || null;
}

/**
 * Verify a UID exists on the server with retries on transient errors.
 * Returns:
 *   - { found: true } if the row exists
 *   - { found: false } if the server confirms the row does NOT exist
 *   - { transient: true } if all retries failed due to network/server issues
 */
export async function verifyUidExists(
  uid: string,
  retries = 2
): Promise<{ found: boolean; transient?: boolean }> {
  if (!validateUidFormat(uid)) return { found: false };

  let lastErr: unknown = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const user = await lookupUserStrict(uid);
      return { found: !!user };
    } catch (err) {
      lastErr = err;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 500));
      }
    }
  }
  console.warn('[verifyUidExists] All retries failed (transient):', lastErr);
  return { found: false, transient: true };
}

// Upload encrypted keys backup to server (for file-free recovery)
// The data is already encrypted with PBKDF2 client-side, so server cannot decrypt
export async function uploadEncryptedKeysBackup(
  uid: string,
  encryptedKeys: string,
  encryptedUnlockCode?: string | null
): Promise<void> {
  if (!validateUidFormat(uid)) return;
  
  const updateData: Record<string, string> = { encrypted_keys_backup: encryptedKeys };
  if (encryptedUnlockCode) {
    updateData.encrypted_unlock_code_backup = encryptedUnlockCode;
  }
  
  try {
    const { secureUpdateUser } = await import('./secureApiClient');
    await secureUpdateUser(uid, updateData);
  } catch {
    if (isOfflineMode()) queueOfflineAction('backup_keys', { uid, encryptedKeys, encryptedUnlockCode });
  }
}

// Fetch encrypted keys backup from server (for recovery on new device)
export async function fetchEncryptedKeysBackup(
  uid: string
): Promise<{ encryptedKeys: string; encryptedUnlockCode: string | null } | null> {
  if (!validateUidFormat(uid)) return null;
  
  const { data, error } = await supabase
    .from('users')
    .select('encrypted_keys_backup, encrypted_unlock_code_backup')
    .eq('uid', uid)
    .single();

  if (error || !data || !data.encrypted_keys_backup) return null;
  return {
    encryptedKeys: data.encrypted_keys_backup,
    encryptedUnlockCode: data.encrypted_unlock_code_backup || null,
  };
}

// Upload encrypted data backup (contacts + messages) to server for recovery
// Data is encrypted client-side with the user's passphrase — server cannot decrypt
export async function uploadEncryptedDataBackup(
  uid: string,
  encryptedData: string
): Promise<void> {
  if (!validateUidFormat(uid)) return;
  
  try {
    const { secureUpdateUser } = await import('./secureApiClient');
    await secureUpdateUser(uid, { encrypted_data_backup: encryptedData });
  } catch {
    if (isOfflineMode()) queueOfflineAction('backup_data', { uid, encryptedData });
  }
}

// Fetch encrypted data backup from server (for recovery on new device)
export async function fetchEncryptedDataBackup(
  uid: string
): Promise<string | null> {
  if (!validateUidFormat(uid)) return null;
  
  const { data, error } = await supabase
    .from('users')
    .select('encrypted_data_backup')
    .eq('uid', uid)
    .single();

  if (error || !data || !data.encrypted_data_backup) return null;
  return data.encrypted_data_backup as string;
}


export async function sendEncryptedMessage(
  senderUid: string,
  recipientUid: string,
  encryptedContent: string,
  nonce: string,
  burnAfterView: boolean = false
): Promise<{ success: boolean; error?: string; id?: string }> {
  assertValidUid(senderUid, 'sender UID');
  assertValidUid(recipientUid, 'recipient UID');
  
  try {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_uid: senderUid,
        recipient_uid: recipientUid,
        encrypted_content: encryptedContent,
        nonce,
        burn_after_view: burnAfterView,
      })
      .select('id')
      .single();

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true, id: data?.id };
  } catch {
    if (isOfflineMode()) {
      queueOfflineAction('send_message', { senderUid, recipientUid, encryptedContent, nonce, burnAfterView });
      return { success: true, id: `offline-${Date.now()}` };
    }
    return { success: false, error: 'Network error' };
  }
}

export async function sendGroupMessage(
  senderUid: string,
  groupId: string,
  encryptedContent: string,
  nonce: string,
  burnAfterView: boolean = false
): Promise<{ success: boolean; error?: string; id?: string }> {
  try {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_uid: senderUid,
        group_id: groupId,
        encrypted_content: encryptedContent,
        nonce,
        burn_after_view: burnAfterView,
      })
      .select('id')
      .single();

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true, id: data?.id };
  } catch {
    if (isOfflineMode()) {
      queueOfflineAction('send_group_message', { senderUid, groupId, encryptedContent, nonce, burnAfterView });
      return { success: true, id: `offline-${Date.now()}` };
    }
    return { success: false, error: 'Network error' };
  }
}

export async function markMessageDelivered(
  messageId: string,
  recipientUid: string,
  signingSecretKey: string
): Promise<boolean> {
  const { signMessage } = await import('./crypto');
  const timestamp = Date.now().toString();
  const signedMessage = `DELIVERED:${messageId}:${timestamp}`;
  const signature = signMessage(signedMessage, signingSecretKey);

  const { data, error } = await supabase.functions.invoke('secure-mark-delivered', {
    body: { messageId, recipientUid, signature, timestamp },
  });

  if (error || !data?.success) {
    console.error('Failed to mark delivered:', error);
    return false;
  }
  return true;
}

export async function fetchDirectMessages(
  userUid: string,
  contactUid: string
): Promise<DbMessage[]> {
  const safeUserUid = sanitizeUid(userUid);
  const safeContactUid = sanitizeUid(contactUid);
  if (!safeUserUid || !safeContactUid) {
    return [];
  }
  
  // Step 1: Fetch lightweight metadata (no encrypted_content) to avoid statement timeouts on large media payloads
  const { data: headers, error } = await supabase
    .from('messages')
    .select('id, sender_uid, recipient_uid, group_id, nonce, created_at, delivered, burn_after_view')
    .or(`and(sender_uid.eq.${safeUserUid},recipient_uid.eq.${safeContactUid}),and(sender_uid.eq.${safeContactUid},recipient_uid.eq.${safeUserUid})`)
    .order('created_at', { ascending: true });

  if (error || !headers) return [];

  // Step 2: Fetch full content in batches (avoid N+1 queries)
  const ids = headers.map(h => h.id);
  const results: DbMessage[] = [];
  const BATCH_SIZE = 50;
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    try {
      const batch = ids.slice(i, i + BATCH_SIZE);
      const { data } = await supabase
        .from('messages')
        .select('*')
        .in('id', batch)
        .order('created_at', { ascending: true });
      if (data) results.push(...(data as DbMessage[]));
    } catch {
      // Batch failed — skip, will retry next time
    }
  }
  return results;
}

export async function fetchGroupMessages(groupId: string): Promise<DbMessage[]> {
  // Step 1: Fetch lightweight metadata
  const { data: headers, error } = await supabase
    .from('messages')
    .select('id, sender_uid, recipient_uid, group_id, nonce, created_at, delivered, burn_after_view')
    .eq('group_id', groupId)
    .order('created_at', { ascending: true });

  if (error || !headers) return [];

  // Step 2: Fetch full content in batches
  const ids = headers.map(h => h.id);
  const results: DbMessage[] = [];
  const BATCH_SIZE = 50;
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    try {
      const batch = ids.slice(i, i + BATCH_SIZE);
      const { data } = await supabase
        .from('messages')
        .select('*')
        .in('id', batch)
        .order('created_at', { ascending: true });
      if (data) results.push(...(data as DbMessage[]));
    } catch {}
  }
  return results;
}

export async function deleteMessage(
  messageId: string,
  senderUid: string,
  signingSecretKey: string
): Promise<boolean> {
  const { signMessage } = await import('./crypto');
  const timestamp = Date.now().toString();
  const signedMessage = `DELETE:${messageId}:${timestamp}`;
  const signature = signMessage(signedMessage, signingSecretKey);

  const { data, error } = await supabase.functions.invoke('secure-delete-message', {
    body: { messageId, senderUid, signature, timestamp },
  });

  if (error || !data?.success) {
    console.error('Failed to delete message:', error);
    return false;
  }
  return true;
}

export async function createGroup(
  name: string,
  topic: string | null,
  createdBy: string
): Promise<{ group?: DbGroup; error?: string }> {
  const { data, error } = await supabase
    .from('groups')
    .insert({
      name,
      topic,
      created_by: createdBy,
    })
    .select('id, name, topic, created_by')
    .single();

  if (error) {
    return { error: error.message };
  }
  return { group: data as DbGroup };
}

export async function addGroupMember(
  groupId: string,
  userUid: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('group_members')
    .insert({
      group_id: groupId,
      user_uid: userUid,
    });

  if (error) {
    if (error.code === '23505') {
      return { success: true };
    }
    return { success: false, error: error.message };
  }
  return { success: true };
}

export async function leaveGroup(
  groupId: string,
  userUid: string,
  signingSecretKey: string
): Promise<{ success: boolean; error?: string }> {
  const { signMessage } = await import('./crypto');
  const timestamp = Date.now().toString();
  const signedMessage = `LEAVE_GROUP:${groupId}:${timestamp}`;
  const signature = signMessage(signedMessage, signingSecretKey);

  const { data, error } = await supabase.functions.invoke('secure-leave-group', {
    body: { groupId, userUid, signature, timestamp },
  });

  if (error || !data?.success) {
    console.error('Failed to leave group:', error);
    return { success: false, error: error?.message || 'Failed to leave group' };
  }
  return { success: true };
}

export async function getUserGroups(userUid: string): Promise<DbGroup[]> {
  const { data, error } = await supabase
    .from('group_members')
    .select('group_id, groups(id, name, topic, created_by)')
    .eq('user_uid', userUid);

  if (error || !data) return [];
  return data.map((item: any) => item.groups).filter(Boolean) as DbGroup[];
}

// Get the count of members in a group
export async function getGroupMemberCount(groupId: string): Promise<number> {
  const { count, error } = await supabase
    .from('group_members')
    .select('*', { count: 'exact', head: true })
    .eq('group_id', groupId);
  if (error || count === null) return 0;
  return count;
}

// ─── Realtime subscriptions with throttled reconnect ──────────────────

/** Throttle: minimum time between channel subscribe attempts */
const MIN_RESUBSCRIBE_INTERVAL_MS = 10_000;
const lastSubscribeAttempt = new Map<string, number>();

function canResubscribe(channelKey: string): boolean {
  const last = lastSubscribeAttempt.get(channelKey) ?? 0;
  const now = Date.now();
  if (now - last < MIN_RESUBSCRIBE_INTERVAL_MS) return false;
  lastSubscribeAttempt.set(channelKey, now);
  return true;
}

// Subscribe to new messages for a user (with polling fallback + self-healing on resume)
export function subscribeToMessages(
  userUid: string,
  signingSecretKey: string,
  onMessage: (message: DbMessage) => void
): () => void {
  const safeUserUid = sanitizeUid(userUid);
  if (!safeUserUid) {
    console.error('Invalid UID format for subscription');
    return () => {};
  }
  
  const sourceId = `user-messages-${safeUserUid}`;
  const seenIds = new Set<string>();
  let pollInterval: ReturnType<typeof setInterval> | null = null;
  // Restore last poll time from storage so messages sent while the app was
  // closed are fetched on next open. Fall back to 24 hours ago.
  const POLL_TIME_KEY = `aphenis_last_poll_${safeUserUid}`;
  const storedPollTime = localStorage.getItem(POLL_TIME_KEY);
  let lastPollTime = storedPollTime || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  let currentChannel: ReturnType<typeof supabase.channel> | null = null;
  let disposed = false;

  const wrappedOnMessage = (msg: DbMessage) => {
    if (seenIds.has(msg.id)) return;
    seenIds.add(msg.id);
    if (seenIds.size > 500) {
      const arr = Array.from(seenIds);
      arr.splice(0, 200).forEach(id => seenIds.delete(id));
    }
    markMessageDelivered(msg.id, userUid, signingSecretKey);
    onMessage(msg);
  };

  // Polling fallback — catches missed realtime events
  // Uses two-step approach: fetch IDs first (lightweight), then full content per-message
  // to avoid statement timeouts on large media payloads
  const POLL_INTERVAL = 3000;
  const pollForMessages = async () => {
    try {
      // Step 1: Fetch only lightweight metadata (no encrypted_content)
      const { data: headers } = await supabase
        .from('messages')
        .select('id, sender_uid, recipient_uid, group_id, nonce, created_at, delivered, burn_after_view')
        .eq('recipient_uid', safeUserUid)
        .gt('created_at', lastPollTime)
        .order('created_at', { ascending: true });

      if (headers && headers.length > 0) {
        lastPollTime = headers[headers.length - 1].created_at;
        safeSetItem(POLL_TIME_KEY, lastPollTime);
        // Step 2: Batch-fetch full content for unseen messages
        const unseenIds = headers.filter(h => !seenIds.has(h.id)).map(h => h.id);
        if (unseenIds.length > 0) {
          try {
            const { data } = await supabase
              .from('messages')
              .select('*')
              .in('id', unseenIds)
              .order('created_at', { ascending: true });
            if (data) {
              for (const msg of data) wrappedOnMessage(msg as DbMessage);
            }
          } catch {
            // Batch failed — will retry next poll
          }
        }
      }
    } catch {
      // Silently fail — next poll will retry
    }
  };
  const startPolling = () => {
    if (pollInterval) return;
    pollInterval = setInterval(pollForMessages, POLL_INTERVAL);
  };

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const createChannel = (retryCount = 0) => {
    if (disposed) return;

    // Clean up old channel if any
    if (currentChannel) {
      try { supabase.removeChannel(currentChannel); } catch {}
      currentChannel = null;
    }

    const channelId = `${sourceId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    currentChannel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_uid=eq.${safeUserUid}`,
        },
        async (payload) => {
          // Don't use payload.new directly — Supabase Realtime truncates large
          // column values (e.g. encrypted_content with inline media), which causes
          // decryption failures. Use realtime as a notification trigger only,
          // then refetch the full row via REST.
          const notif = payload.new as DbMessage;
          if (!notif?.id || seenIds.has(notif.id)) return;
          try {
            const { data } = await supabase
              .from('messages')
              .select('*')
              .eq('id', notif.id)
              .single();
            if (data) wrappedOnMessage(data as DbMessage);
          } catch {
            // REST fetch failed — polling will retry
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setConnectionStatus('connected', sourceId);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setConnectionStatus('reconnecting', sourceId);
          // Auto-retry with backoff (max 3 retries)
          if (!disposed && retryCount < 3) {
            const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
            console.log(`[subscribeToMessages] Channel ${status}, retrying in ${delay}ms (attempt ${retryCount + 1})`);
            setTimeout(() => createChannel(retryCount + 1), delay);
          }
        }
      });
  };

  // Create initial channel
  createChannel();

  // Always start polling as a fallback + immediate first poll
  startPolling();
  pollForMessages();

  // Self-heal on app resume: tear down stale channel and recreate
  const unsubResume = onAppResume(() => {
    if (disposed) return;
    console.log('[subscribeToMessages] Recreating channel on resume');
    createChannel();
    // Restart polling (timers freeze in background)
    stopPolling();
    startPolling();
    // Immediate poll to catch anything missed while backgrounded
    pollForMessages();
  });

  // Listen for manual refresh triggers (e.g. pull-to-refresh on chat list)
  const handleManualPoll = () => {
    if (!disposed) pollForMessages();
  };
  window.addEventListener('aphenis-force-poll', handleManualPoll);

  return () => {
    disposed = true;
    stopPolling();
    unsubResume();
    window.removeEventListener('aphenis-force-poll', handleManualPoll);
    if (currentChannel) {
      supabase.removeChannel(currentChannel);
      currentChannel = null;
    }
    removeConnectionSource(sourceId);
  };
}

// Subscribe to group messages (with polling fallback + self-healing on resume)
export function subscribeToGroupMessages(
  groupId: string,
  onMessage: (message: DbMessage) => void
): () => void {
  const sourceId = `group-messages-${groupId}`;
  const seenIds = new Set<string>();
  let pollInterval: ReturnType<typeof setInterval> | null = null;
  let lastPollTime = new Date().toISOString();
  let currentChannel: ReturnType<typeof supabase.channel> | null = null;
  let disposed = false;

  const wrappedOnMessage = (msg: DbMessage) => {
    if (seenIds.has(msg.id)) return;
    seenIds.add(msg.id);
    if (seenIds.size > 500) {
      const arr = Array.from(seenIds);
      arr.splice(0, 200).forEach(id => seenIds.delete(id));
    }
    onMessage(msg);
  };

  const pollForGroupMessages = async () => {
    try {
      const { data: headers } = await supabase
        .from('messages')
        .select('id, sender_uid, recipient_uid, group_id, nonce, created_at, delivered, burn_after_view')
        .eq('group_id', groupId)
        .gt('created_at', lastPollTime)
        .order('created_at', { ascending: true });

      if (headers && headers.length > 0) {
        lastPollTime = headers[headers.length - 1].created_at;
        const unseenIds = headers.filter(h => !seenIds.has(h.id)).map(h => h.id);
        if (unseenIds.length > 0) {
          try {
            const { data } = await supabase
              .from('messages')
              .select('*')
              .in('id', unseenIds)
              .order('created_at', { ascending: true });
            if (data) {
              for (const msg of data) wrappedOnMessage(msg as DbMessage);
            }
          } catch {}
        }
      }
    } catch {}
  };
  const startPolling = () => {
    if (pollInterval) return;
    pollInterval = setInterval(pollForGroupMessages, 5000);
  };

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const createChannel = (retryCount = 0) => {
    if (disposed) return;

    if (currentChannel) {
      try { supabase.removeChannel(currentChannel); } catch {}
      currentChannel = null;
    }

    const channelId = `${sourceId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    currentChannel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `group_id=eq.${groupId}`,
        },
        async (payload) => {
          // Refetch full row — realtime truncates large encrypted_content
          const notif = payload.new as DbMessage;
          if (!notif?.id || seenIds.has(notif.id)) return;
          try {
            const { data } = await supabase
              .from('messages')
              .select('*')
              .eq('id', notif.id)
              .single();
            if (data) wrappedOnMessage(data as DbMessage);
          } catch {}
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setConnectionStatus('connected', sourceId);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setConnectionStatus('reconnecting', sourceId);
          if (!disposed && retryCount < 3) {
            const delay = Math.min(2000 * Math.pow(2, retryCount), 10000);
            console.log(`[subscribeToGroupMessages] Channel ${status}, retrying in ${delay}ms (attempt ${retryCount + 1})`);
            setTimeout(() => createChannel(retryCount + 1), delay);
          }
        }
      });
  };

  createChannel();
  startPolling();
  pollForGroupMessages();

  // Self-heal on resume
  const unsubResume = onAppResume(() => {
    if (disposed) return;
    console.log('[subscribeToGroupMessages] Recreating channel on resume');
    createChannel();
    stopPolling();
    startPolling();
    pollForGroupMessages();
  });

  // Listen for manual refresh triggers
  const handleManualPoll = () => {
    if (!disposed) pollForGroupMessages();
  };
  window.addEventListener('aphenis-force-poll', handleManualPoll);

  return () => {
    disposed = true;
    stopPolling();
    unsubResume();
    window.removeEventListener('aphenis-force-poll', handleManualPoll);
    if (currentChannel) {
      supabase.removeChannel(currentChannel);
      currentChannel = null;
    }
    removeConnectionSource(sourceId);
  };
}

/** Trigger an immediate poll cycle across all active subscriptions */
export function triggerManualPoll(): void {
  window.dispatchEvent(new Event('aphenis-force-poll'));
}

// Send a contact request
export async function sendContactRequest(
  senderUid: string,
  recipientUid: string
): Promise<{ success: boolean; error?: string }> {
  assertValidUid(senderUid, 'sender UID');
  assertValidUid(recipientUid, 'recipient UID');

  const { error } = await supabase
    .from('contact_requests')
    .insert({ sender_uid: senderUid, recipient_uid: recipientUid });

  if (error) {
    if (error.code === '23505') return { success: true };
    return { success: false, error: error.message };
  }
  return { success: true };
}

// Cancel a pending contact request
export async function cancelContactRequest(
  senderUid: string,
  recipientUid: string
): Promise<{ success: boolean; error?: string }> {
  assertValidUid(senderUid, 'sender UID');
  assertValidUid(recipientUid, 'recipient UID');

  const { error } = await supabase
    .from('contact_requests')
    .delete()
    .eq('sender_uid', senderUid)
    .eq('recipient_uid', recipientUid)
    .eq('status', 'pending');

  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
}

// Fetch pending contact requests for a user
export async function fetchContactRequests(
  userUid: string
): Promise<{ id: string; sender_uid: string; recipient_uid: string; status: string; created_at: string }[]> {
  const safeUid = sanitizeUid(userUid);
  if (!safeUid) return [];

  const { data, error } = await supabase
    .from('contact_requests')
    .select('*')
    .or(`sender_uid.eq.${safeUid},recipient_uid.eq.${safeUid}`)
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data as any[];
}

// Respond to a contact request (accept/reject via edge function)
export async function respondToContactRequest(
  requestId: string,
  recipientUid: string,
  action: 'accepted' | 'rejected',
  signingSecretKey: string
): Promise<boolean> {
  const { signMessage } = await import('./crypto');
  const timestamp = Date.now().toString();
  const signedMessage = `CONTACT_REQUEST:${action}:${requestId}:${timestamp}`;
  const signature = signMessage(signedMessage, signingSecretKey);

  const { data, error } = await supabase.functions.invoke('respond-contact-request', {
    body: { requestId, recipientUid, action, signature, timestamp },
  });

  if (error || !data?.success) {
    console.error('Failed to respond to contact request:', error);
    return false;
  }
  return true;
}

// Subscribe to contact requests for a user (with self-healing)
export function subscribeToContactRequests(
  userUid: string,
  onRequest: (request: any) => void
): () => void {
  const safeUid = sanitizeUid(userUid);
  if (!safeUid) return () => {};

  let currentChannel: ReturnType<typeof supabase.channel> | null = null;
  let disposed = false;

  const createChannel = (retryCount = 0) => {
    if (disposed) return;
    if (currentChannel) {
      try { supabase.removeChannel(currentChannel); } catch {}
      currentChannel = null;
    }

    const channelId = `contact-requests-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    currentChannel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_requests',
          filter: `recipient_uid=eq.${safeUid}`,
        },
        (payload) => {
          onRequest(payload.new);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contact_requests',
          filter: `sender_uid=eq.${safeUid}`,
        },
        (payload) => {
          onRequest(payload.new);
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          if (!disposed && retryCount < 3) {
            setTimeout(() => createChannel(retryCount + 1), Math.min(2000 * Math.pow(2, retryCount), 10000));
          }
        }
      });
  };

  createChannel();

  const unsubResume = onAppResume(() => {
    if (disposed) return;
    createChannel();
  });

  return () => {
    disposed = true;
    unsubResume();
    if (currentChannel) supabase.removeChannel(currentChannel);
  };
}

// Subscribe to delivery status updates for messages sent by this user (with self-healing)
export function subscribeToDeliveryUpdates(
  senderUid: string,
  onDelivered: (messageId: string) => void
): () => void {
  const safeSenderUid = sanitizeUid(senderUid);
  if (!safeSenderUid) return () => {};

  let currentChannel: ReturnType<typeof supabase.channel> | null = null;
  let disposed = false;

  const createChannel = (retryCount = 0) => {
    if (disposed) return;
    if (currentChannel) {
      try { supabase.removeChannel(currentChannel); } catch {}
      currentChannel = null;
    }

    const channelId = `delivery-updates-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    currentChannel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `sender_uid=eq.${safeSenderUid}`,
        },
        (payload) => {
          const msg = payload.new as DbMessage;
          if (msg.delivered) {
            onDelivered(msg.id);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          if (!disposed && retryCount < 3) {
            setTimeout(() => createChannel(retryCount + 1), Math.min(2000 * Math.pow(2, retryCount), 10000));
          }
        }
      });
  };

  createChannel();

  const unsubResume = onAppResume(() => {
    if (disposed) return;
    createChannel();
  });

  return () => {
    disposed = true;
    unsubResume();
    if (currentChannel) supabase.removeChannel(currentChannel);
  };
}
